/**
 * Theme preference: storage, resolution, and applying it to the document.
 *
 * Three preferences, two outcomes. `system` is a preference, never a rendered
 * state — it is resolved to `light` or `dark` before it ever reaches the DOM,
 * so the stylesheet only has to know about two themes. See the `@custom-variant`
 * note at the top of `styles/index.css`.
 *
 * Pure module: no React. The hook lives in `app/use-theme.ts` so that this can
 * also be called from the pre-paint script's TypeScript twin and from tests.
 */

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/** Shared with the inline script in index.html — change both or neither. */
export const THEME_STORAGE_KEY = "aa.theme";

const PREFERENCES: readonly ThemePreference[] = ["light", "dark", "system"];

const DARK_QUERY = "(prefers-color-scheme: dark)";

function isPreference(value: unknown): value is ThemePreference {
  return typeof value === "string" && (PREFERENCES as readonly string[]).includes(value);
}

/**
 * The default when nothing has been chosen.
 *
 * `light`, not `system`. The console is designed light — white panels on a grey
 * canvas, tinted icon tiles, soft shadows — so an operator whose OS is in dark
 * mode should still get the tool as intended. Picking "System" in the toggle
 * opts back into following the OS.
 *
 * Mirrored by the pre-paint script in index.html. Change both or neither.
 */
const DEFAULT_PREFERENCE: ThemePreference = "light";

/**
 * The stored preference, or the default when there is none.
 *
 * Reads defensively: `localStorage` throws outright in a browser configured to
 * block site data, and an admin panel must not fail to boot over a colour
 * scheme.
 */
export function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : DEFAULT_PREFERENCE;
  } catch {
    return DEFAULT_PREFERENCE;
  }
}

/**
 * Whether this browser holds an explicit choice.
 *
 * Distinct from `readPreference()`, which cannot tell "nothing stored" from
 * "stored as light" — both come back as `light`. The account-level theme is
 * adopted only when this is false, so a preference someone set on *this*
 * machine is never overwritten by the one their account happens to carry.
 */
export function hasStoredPreference(): boolean {
  try {
    return isPreference(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return false;
  }
}

export function writePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Preference is lost on reload; the session still themes correctly.
  }
}

export function systemTheme(): ResolvedTheme {
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === "system" ? systemTheme() : preference;
}

/**
 * Writes the resolved theme to <html>.
 *
 * `data-theme` is what every rule in the stylesheet keys off. The `colorScheme`
 * assignment is belt-and-braces for the brief moment before the CSS lands, so
 * native widgets and the scrollbar do not flash the wrong scheme.
 */
export function applyTheme(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}

/**
 * Subscribes to OS-level scheme changes.
 *
 * Fires regardless of the current preference — the caller decides whether to
 * act on it. Returns an unsubscribe function.
 */
export function watchSystemTheme(onChange: (theme: ResolvedTheme) => void): () => void {
  const query = window.matchMedia(DARK_QUERY);
  const handler = (event: MediaQueryListEvent) => onChange(event.matches ? "dark" : "light");
  query.addEventListener("change", handler);
  return () => query.removeEventListener("change", handler);
}

/** Light → Dark → System → Light. The order the toggle cycles through. */
export function nextPreference(current: ThemePreference): ThemePreference {
  const index = PREFERENCES.indexOf(current);
  return PREFERENCES[(index + 1) % PREFERENCES.length] ?? "system";
}

export const PREFERENCE_LABEL: Record<ThemePreference, string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};
